import {
    DndContext,
    DragEndEvent,
    DragOverEvent,
    DragOverlay,
    DragStartEvent,
    PointerSensor,
    UniqueIdentifier,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import React, {useEffect, useRef, useState} from "react";
import './styles.css'
import '../../../components/journey/create-journey-from-travel-components/styles.css'
import {ACTIVITY_TYPE, AllActivityTypes, AllActivityTypesArray,} from "../../../types-enums/activity";
import {cloneDeep, isEqual, maxBy} from "lodash";
import {v4 as uuidv4} from "uuid";
import DraggingActivityListItem
    from "../../../components/journey/create-journey-from-travel-components/dragging-activity-list-item/DraggingActivityListItem";
import {Fieldset} from "atmosfair-ui";
import {VALIDATE_FORM_STARTING_NUMBER} from "../../../constants/default-values";
import {isActivityCarBusState, isActivityHotelState} from "../../../utils/check/type-guards";
import {DateTime} from "luxon";
import {useTranslation} from "react-i18next";
import {toast, ToastItem, TypeOptions} from "react-toastify";
import ToastWithTextOnly from "../../../molecules/toasts/toast-with-text-only/ToastWithTextOnly";
import CreateJourneyFromTravelComponents, {
    ALL_ACTIVITIES
} from "../../../components/journey/create-journey-from-travel-components/create-journey-from-travel-components/CreateJourneyFromTravelComponents";
import JourneyOverview from "../../../components/journey/journey-overview/journey-overview/JourneyOverview";
import {
    deleteActivityFromJourney,
    deleteActivityFromJourneyActivityQueue,
    deleteDayFromJourney,
    deleteDayFromJourneyDayQueue,
    JourneyState,
    replaceActivitiesInJourney,
    undoDeleteActivityFromJourney,
    undoDeleteDayFromJourney
} from "../../../redux/reducer/journey";
import {store, useAppDispatch} from "../../../redux/reducer/store";
import {hasExactDates} from "../../../utils/getter/journey";
import {mergeTravelComponentsToJourneyElements} from "../../../utils/converter/travel-components";
import {DatesProps} from "../../../types-enums/activity-common-properties";
import ToastWithButton from "../../../molecules/toasts/toast-with-button/ToastWithButton";
import {convertUniqueIdentifierToNumber, isValidDay,} from "../../../components/journey/common";
import {createArrayFromNumber} from "../../../utils/common";
import {generateTranslatedActivityTypeString} from "../../../utils/string/common";
import {changeDayOfActivitiesOnLastDayOfJourney} from "../../../redux/reducer/journey-utils";
import {blockChangeJourneyDuration} from "../../../redux/reducer/settings";
import {getDurationInDaysOrNights} from "../../../utils/getter/common";
import {
    changeDateOnDayChange,
    groupActivities,
    recalculateActivityOnDifferentDate
} from "../../../utils/check/activities";
import useRecalculateActivityOnDifferentDate from "../../../hooks/use-recalculate-activity-on-different-date";
import {
    activityToJourneyStateWhenCreateJourneyFromTravelComponents,
    addDatesToActivities,
    addDatesToActivitiesWhenChangeDateOnDayChange,
    removeDatesFromActivitiesWhenChangeDateOnDayChange,
    removeRouteAndWaypointsFromCarBusActivity
} from "../../../utils/setter/activities";
import {hasCarBusRouteAndCountDaysBiggerOne} from "../../../components/journey/journey-overview/common";

export enum JOURNEY_DND_CONTAINER_WHICH_VIEW {
    createFromTravelComponents = 'createFromTravelComponents',
    journeyOverview = 'journeyOverview'
}

export interface DnDContainerProps {
    whichView: JOURNEY_DND_CONTAINER_WHICH_VIEW
    activities: AllActivityTypesArray,
    travelComponentActivities?: AllActivityTypesArray
    journeyDuration: JourneyState["journeyDuration"],
    validateForm?: number,
    allowCalculation: boolean,
    onFormIsValid?: (activities: AllActivityTypesArray) => void
    journeyPersonCount: number
}

export type WhichArrowProps = 'up' | 'down'

/**
 * General
 * Implements some common functionalities that are shared between two screens: EditJourney and SaveBundleCreateJourneyDialogue
 * The main common functionality is: moving of activities via arrow or drag & drop and subsequent recalculating of activities emissions if they were moved AND if the journey has an exact date
 * Especially the journeyOverview has some special functionalities like moving a day, deleting a day, bulk moving of
 * activities if the alt key is not pressed etc. However, all the actions require (if the journey has an exact date) a
 * recalculation of the activities emissions. Basically this component is a wrapper to implement these functionalities.
 *
 * whichView === createFromTravelComponents
 * One can drag activities from the very top box and add the dragged activity to a day. The dragged activity
 * won't be removed from the very top box. Since the drag and dropped activity is not removed from the top box,
 * and added to a day - this means - multiple activities of the same type can be added to the same day, different days, ...
 * Since there can be multiple activities, various ID's are introducted:
 * ID: Unique, gets reassigned once an activity is moved from top box to a day
 * Semantic ID: Basically is the original ID of the dragged element and is used to group same activities
 * Group ID: ID that gets assigned by mergeTravelComponentsToJourneyElements() to group activities

 * whichView === journeyOverview
 * same functionality as whichView === createFromTravelComponents, with the difference that there is no top box,
 * but only days between which the Activities can be moved.

 * Recalculation on Drag and Drop (or move via button)
 * The emissions of some activities (like Flight) can depend on the date. If you have searched a flight with flight number and date,
 * and drag and drop the flight to a different date, the emissions must to be recalulated with the new data (here: new date).
 * It can happen that the flight with flight number and (new) flight date cannot be found on recalculation.
 * Then, a fallback cascade kicks in and removes the flight number from the flight and tries to calculate the emissions again without flight number.
 */
const JourneyDnDContainer = ({
                                 activities,
                                 validateForm,
                                 onFormIsValid,
                                 journeyDuration,
                                 whichView,
                                 allowCalculation,
                                 travelComponentActivities,
                                 journeyPersonCount
                             }: DnDContainerProps) => {
    const [dndActivities, setDnDActivities] = useState<AllActivityTypesArray>([])
    const [journeyDurationInDays, setJourneyDurationInDays] = useState(getDurationInDaysOrNights(journeyDuration, false)!!)
    const [draggingActivity, setDraggingActivity] = useState<AllActivityTypes | null>(null)
    const [draggingOverDay, setDraggingOverDay] = useState<UniqueIdentifier | null>(null)
    const [altKeyPressed, setAltKeyPressed] = useState(false)
    const onDragStartDroppableZoneId = useRef<string | null>(null)
    const draggingActivityRef = useRef<AllActivityTypes | null>(null)
    const {t, i18n} = useTranslation()
    const journeyHasExactDates = hasExactDates(journeyDuration)
    const originalActivitiesForCreateFromTravelComponentsView = useRef(cloneDeep(activities))
    const dndActivitiesRef = useRef<AllActivityTypesArray>(activities)
    const journeyDurationRef = useRef(journeyDuration)
    const previousDatesCalculatedWith = useRef<DatesProps | null>(journeyDuration.dates)
    const avoidRecalculationThroughUseEffect = useRef(false)
    const toastCount = useRef(0)
    const dispatch = useAppDispatch()
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 10,
            },
        })
    )
    const {
        isLoading,
        updatedActivities,
        recalculateActivitiesEmissionsWithUpdatedDateInActivityProps,
        recalculateActivityEmissionWitNewGivenDay
    } = useRecalculateActivityOnDifferentDate({
        journeyDuration,
        renderToast: (message, type) => _renderToast(message, type),
    })

    const _renderToast = (message: string, type: TypeOptions) => toast(<ToastWithTextOnly
            message={message}/>,
        {progress: undefined, type}
    )

    useEffect(() => {
        if (updatedActivities) _updateActivitiesObjectsBasedOnView(updatedActivities)
    }, [updatedActivities]);

    useEffect(() => {
        activities.forEach(activity => {
            const {common: {activityType}} = activity
            if (groupActivities(activityType) && recalculateActivityOnDifferentDate(activityType)) console.warn("Hey Developer 👋, looks like you need to implement a new functionality 🤖. Up to now, there never as the situation where an activity had to ge grouped (like i.e. Hotels) AND be recalculated when the date changes. Apperently this is different now because groupActivities() and recalculateActivityOnDifferentDate() both are true. The reason why this is currently not implemented is the following: When an activity needs to be recalculated based on the date, you must execute _recalculateOnDateChange(). We also must re-grouped the activities, because they have been moved from one day to another (with this function mergeTravelComponentsToJourneyElements()). However, by executing this mergeTravelComponentsToJourneyElements() you get new common.ids for the activities, which makes it impossible to map the result of _recalculateOnDateChange() to the activities. And thats exactly what needs to be implemented. Happy coding! 😻")
        })
    }, [])

    useEffect(() => {
        window.addEventListener('keydown', event => {
            if (event.key === "Alt") _handleKeyDown()
        })
        return () => window.removeEventListener("keydown", _handleKeyDown)
    }, [])

    useEffect(() => {
        window.addEventListener('keyup', event => {
            if (event.key === "Alt") _handleKeyUp()
        })
        return () => window.removeEventListener("keyup", _handleKeyUp)
    }, [])

    useEffect(() => {
        if (typeof validateForm !== 'undefined' && validateForm > VALIDATE_FORM_STARTING_NUMBER) {
            onFormIsValid && onFormIsValid(dndActivities)
        }
    }, [validateForm])

    useEffect(() => {
        if (whichView === JOURNEY_DND_CONTAINER_WHICH_VIEW.journeyOverview) _setDndActivities(activities)
    }, [activities])

    /**
     * This useEffect() reacts to any changes in the journeyDuration, which means, also deleting a day.
     * This has some side effects i.e. which activities should be recalculated if a day is deleted.
     * Better: This useEffect() should only react to changes in the journeyDuration when these changes
     * where done through the calendar (i.e. via the SiteTitle)
     * In order to handle this, this useEffect() uses some variables to determine what triggered the change in the
     * jounrneyDuration and thus handles only the case, when the duration of the journey was changes through the calendar.
     */
    useEffect(() => {
        journeyDurationRef.current = journeyDuration
        let updatedDndActivities = cloneDeep(dndActivitiesRef.current)
        setJourneyDurationInDays(getDurationInDaysOrNights(journeyDuration, false)!!)
        let recalculate = false
        const {dates} = journeyDuration
        const differentDate = !isEqual(dates, previousDatesCalculatedWith.current) && allowCalculation
        switch (whichView) {
            case JOURNEY_DND_CONTAINER_WHICH_VIEW.createFromTravelComponents:
                if (journeyHasExactDates) {
                    addDatesToActivities(updatedDndActivities, dates!!.startDate, journeyDuration)
                    // updateDatesInActivities(updatedDndActivities, DateTime.fromMillis(dates!!.startDateMilliseconds))
                    // In JourneyDnDContainerWhichView.createFromTravelComponents, days can only be added or removed from the
                    // end of the journey. Because of that, a recalculation is only needed if the start date changes
                    if (!isEqual(dates?.startDate, previousDatesCalculatedWith.current?.startDate)) recalculate = true
                } else {
                    removeDatesFromActivitiesWhenChangeDateOnDayChange(updatedDndActivities)
                    updatedDndActivities = updatedDndActivities.filter(activity => createArrayFromNumber(journeyDurationInDays).includes(activity.common.day!!))
                }
                _updateActivitiesObjectsBasedOnView(updatedDndActivities)
                break;
            case JOURNEY_DND_CONTAINER_WHICH_VIEW.journeyOverview:
                // In JourneyDnDContainerWhichView.journey, a day can be deleted at any "point" of the journey
                // What needs to be recalculated etc. is i.e. handled in the deleteDay() function
                if (!avoidRecalculationThroughUseEffect.current) recalculate = true
                break;
            default:
                whichView satisfies never;
        }

        if (recalculate && differentDate) {
            previousDatesCalculatedWith.current = dates
            recalculateActivitiesEmissionsWithUpdatedDateInActivityProps(updatedDndActivities)
        }
        avoidRecalculationThroughUseEffect.current = false
    }, [journeyDuration, allowCalculation])

    const _handleKeyUp = () => setAltKeyPressed(false)

    const _handleKeyDown = () => setAltKeyPressed(true)

    /**
     * This function updates the Activities used for drag and drop. It also adjusts the CO2KgE for Activities, that
     * can have a duration of more than one day.
     */
    const _setDndActivities = (updatedDndActivities: AllActivityTypesArray) => {
        dndActivitiesRef.current = updatedDndActivities
        setDnDActivities(updatedDndActivities)
    }

    /**
     * Dependent on where JourneyDnDContainer is used, this function updates the data objects.
     */
    const _updateActivitiesObjectsBasedOnView = (updatedDndActivities: AllActivityTypesArray) => {
        switch (whichView) {
            case JOURNEY_DND_CONTAINER_WHICH_VIEW.createFromTravelComponents:
                setDnDActivities(updatedDndActivities)
                break;
            case JOURNEY_DND_CONTAINER_WHICH_VIEW.journeyOverview:
                dispatch(replaceActivitiesInJourney(updatedDndActivities))
                break;
            default:
                whichView satisfies never
        }
        dndActivitiesRef.current = updatedDndActivities
    }

    /**
     * Updates the day and conditionally the date in an activity. Do not confuse with setting a new date for recalculation
     * of an activity. This is done in _updateActivityRequest()
     */
    const _updateDatesInActivityOnDeleteOrReAddDay = (
        activityToUpdate: AllActivityTypes,
        addOrRemoveOneDay: "add" | "remove",
    ) => {
        const {common: {activityType}} = activityToUpdate
        if (changeDateOnDayChange(activityType)) {
            if (isActivityHotelState(activityToUpdate)) {
                const {hotelStayDuration} = activityToUpdate
                if (hasExactDates(hotelStayDuration)) {
                    if (hotelStayDuration.dates !== null) {
                        const {startDate, endDate} = hotelStayDuration.dates
                        let updatedEndDate: DateTime
                        let updatedStartDate: DateTime
                        if (addOrRemoveOneDay === "add") {
                            updatedStartDate = startDate.plus({days: 1})
                            updatedEndDate = endDate.plus({days: 1})
                        } else {
                            updatedStartDate = startDate.minus({days: 1})
                            updatedEndDate = endDate.minus({days: 1})
                        }
                        activityToUpdate.hotelStayDuration.dates!!.startDate = updatedEndDate
                        activityToUpdate.hotelStayDuration.dates!!.endDate = updatedStartDate
                    }
                }
            }
        }
    }

    /**
     * Returns boolean for the Activities that can stay in the journey based on the journey duration (when journey duration changes
     * from SiteTitle.tsx). This is needed because i.e. hotels are stays over night, if the journey is only one day long,
     * a hotel cannot be in the journey.
     */
    const _filterActivitiesBasedOnJourneyDuration = (activity: AllActivityTypes, dayToCompare: number, journeyDurationInDays: number) => {
        const {common: {day, activityType}} = activity
        if (day !== null) {
            if (journeyDurationInDays === 1) {
                switch (activityType) {
                    case ACTIVITY_TYPE.hotel:
                        return false
                    case ACTIVITY_TYPE.flight:
                        return true
                    case ACTIVITY_TYPE.car_bus:
                        return true
                        break;
                    default:
                        activityType satisfies never;
                }
            } else {
                if (day !== dayToCompare) return true
            }
        }
    }

    /**
     * Deletes a day in a journey
     */
    const _deleteDay = (dayToDelete: number) => {
        const queueId = uuidv4()
        dispatch(deleteDayFromJourney({day: dayToDelete, id: queueId}))
        dispatch(blockChangeJourneyDuration(true))
        avoidRecalculationThroughUseEffect.current = true
        toastCount.current += 1
        const currentJourneyDurationInDays = getDurationInDaysOrNights(journeyDuration, false)
        if (currentJourneyDurationInDays !== null) {
            let updatedDndActivities = cloneDeep(dndActivitiesRef.current).filter(updatedDndActivity => updatedDndActivity.common.day !== dayToDelete)
            const newDurationInDays = currentJourneyDurationInDays - 1
            if (currentJourneyDurationInDays !== dayToDelete) {
                updatedDndActivities = updatedDndActivities.map(updatedDndActivity => {
                    if (updatedDndActivity.common.day !== null && updatedDndActivity.common.day >= dayToDelete) {
                        updatedDndActivity.common.day -= 1
                        _updateDatesInActivityOnDeleteOrReAddDay(updatedDndActivity, "remove")
                    }
                    return updatedDndActivity
                })
                if (journeyHasExactDates) _recalculateActivitiesOnDeleteDay(updatedDndActivities, dayToDelete)
            } else {
                updatedDndActivities = updatedDndActivities
                    .filter(updatedDndActivity => _filterActivitiesBasedOnJourneyDuration(updatedDndActivity, dayToDelete, newDurationInDays))
                    .map(activity => {
                        const {common: {day}} = activity
                        if (day !== null) {
                            if (day === 1) return activity
                            if (day > dayToDelete) activity.common.day!! -= 1
                        }
                        return activity
                    })

                updatedDndActivities = changeDayOfActivitiesOnLastDayOfJourney(updatedDndActivities, newDurationInDays)
            }
            _mergeAndDispatchReplaceActivitiesInJourney(updatedDndActivities, newDurationInDays)
        }
        _renderDeleteDayToast(queueId, dayToDelete)
    }

    const _mergeAndDispatchReplaceActivitiesInJourney = (updatedDndActivities: AllActivityTypesArray, journeyDurationInDays: number) => {
        const mergedUpdatedDndActivities = mergeTravelComponentsToJourneyElements(
            cloneDeep(updatedDndActivities),
            journeyDurationInDays,
            false,
            false
        )
        dndActivitiesRef.current = mergedUpdatedDndActivities
        dispatch(replaceActivitiesInJourney(mergedUpdatedDndActivities))
    }

    const _forceMoveActivityGroup = (activity: AllActivityTypes) => {
        const {common: {activityType}} = activity
        switch (activityType) {
            case ACTIVITY_TYPE.flight:
                return false
            case ACTIVITY_TYPE.hotel:
                return false
            case ACTIVITY_TYPE.car_bus:
                if (isActivityCarBusState(activity)) return hasCarBusRouteAndCountDaysBiggerOne(activity)
                return false
            default:
                activityType satisfies never
        }
    }

    const _onActivityArrow = (
        droppableZoneId: UniqueIdentifier,
        whichArrow: WhichArrowProps,
        movedActivity: AllActivityTypes,
    ) => {
        let updatedDndActivities = cloneDeep(dndActivitiesRef.current)
        if (droppableZoneId === ALL_ACTIVITIES && whichView === JOURNEY_DND_CONTAINER_WHICH_VIEW.createFromTravelComponents) {
            const newDroppableZoneId = 1
            const updatedMovedActivity = cloneDeep(movedActivity)
            updatedMovedActivity.common.day = newDroppableZoneId
            activityToJourneyStateWhenCreateJourneyFromTravelComponents(journeyDuration, updatedMovedActivity)
            updatedDndActivities.push(updatedMovedActivity)
            _onActivityMoveEnd(updatedMovedActivity, updatedDndActivities, newDroppableZoneId, ALL_ACTIVITIES)
            _setDndActivities(updatedDndActivities)
        } else {
            const newDay = _getNewDroppableZoneIdAndDay(whichArrow, droppableZoneId)
            const currentDay = convertUniqueIdentifierToNumber(droppableZoneId)
            let updatedMovedActivity = cloneDeep(movedActivity)
            let activityFound = false
            const {
                common: {
                    groupId: movedActivityGroupId,
                    id: movedActivityId,
                    activityType
                }
            } = movedActivity
            const foreMoveActivityGroup = _forceMoveActivityGroup(movedActivity)
            if ((altKeyPressed || movedActivityGroupId === null) && !foreMoveActivityGroup) {
                const indexOfMovedActivityAtDay = updatedDndActivities.findIndex(updatedDnDActivity => updatedDnDActivity.common.id === movedActivityId)
                if (indexOfMovedActivityAtDay > -1) {
                    activityFound = true
                    updatedMovedActivity.common.day = newDay
                    updatedDndActivities[indexOfMovedActivityAtDay] = updatedMovedActivity
                }
            } else {
                updatedDndActivities.forEach(updatedDnDActivity => {
                    const {common: {groupId: updatedDnDActivityGroupId}} = updatedDnDActivity
                    if (movedActivityGroupId === updatedDnDActivityGroupId && updatedDnDActivity.common.day !== null) {
                        if (newDay > currentDay) updatedDnDActivity.common.day += 1
                        if (newDay < currentDay) updatedDnDActivity.common.day -= 1
                    }
                })
            }
            if (journeyHasExactDates) addDatesToActivitiesWhenChangeDateOnDayChange(updatedDndActivities, journeyDuration.dates!!.startDate)
            switch (whichView) {
                case JOURNEY_DND_CONTAINER_WHICH_VIEW.journeyOverview:
                    _mergeAndDispatchReplaceActivitiesInJourney(updatedDndActivities, journeyDurationInDays)
                    break;
                case JOURNEY_DND_CONTAINER_WHICH_VIEW.createFromTravelComponents:
                    _setDndActivities(updatedDndActivities)
                    break;
                default:
                    whichView satisfies never;
            }

            if (journeyHasExactDates && activityFound && recalculateActivityOnDifferentDate(activityType)) recalculateActivitiesEmissionsWithUpdatedDateInActivityProps([updatedMovedActivity])
        }
    }

    /**
     *  Function that is called whenever a dragged element is over something.
     */
    const _onDragOver = (event: DragOverEvent) => {
        const {over} = event
        if (over && onDragStartDroppableZoneId.current && isValidDay(onDragStartDroppableZoneId.current)) setDraggingOverDay(over.id)
    }

    /**
     * Function that is called when the dragging of an element starts
     */
    const _onDragStart = (event: DragStartEvent) => {
        const {data: {current}} = event.active
        if (current) {
            const {activity: draggingActivity, droppableZoneId} = current
            if (droppableZoneId) onDragStartDroppableZoneId.current = droppableZoneId
            draggingActivityRef.current = draggingActivity
            setDraggingActivity(draggingActivity)
        }
    }

    /**
     * Function that is called when the user releases the dragged element.
     * Updates the activities and recalculates emissions if needed.
     */
    const _onDragEnd = (event: DragEndEvent) => {
        const {over, active} = event
        const {id: draggedActivityId, data: {current}} = active
        let recalculateOnDifferentDate = false
        if (over) {
            const resultingDroppableZoneId = over.id
            let updatedDndActivities = cloneDeep(dndActivitiesRef.current)
            if (onDragStartDroppableZoneId.current !== resultingDroppableZoneId && current) {
                if (resultingDroppableZoneId === ALL_ACTIVITIES) {
                    updatedDndActivities = updatedDndActivities.filter(updatedDnDActivity => updatedDnDActivity.common.id !== draggedActivityId)
                    setDraggingOverDay(null)
                } else {
                    const draggedActivity = draggingActivityRef.current
                    if (draggedActivity) {
                        recalculateOnDifferentDate = _recalculateActivityOnDifferentDates(draggedActivity)
                        const {common: {activityType}} = draggedActivity
                        let allowDropping = true
                        // Depending on the Activity, it must be forbidden to drop the Activity on the last day
                        // This is i.e. true for hotels, because hotels are over night
                        switch (activityType) {
                            case ACTIVITY_TYPE.flight:
                                break;
                            case ACTIVITY_TYPE.hotel:
                                if (convertUniqueIdentifierToNumber(resultingDroppableZoneId) === journeyDurationInDays) allowDropping = false
                                break;
                            case ACTIVITY_TYPE.car_bus:
                                break;
                            default:
                                activityType satisfies never;
                        }
                        if (allowDropping) {
                            const updatedDraggedActivity = cloneDeep(draggedActivity)
                            const newDay = convertUniqueIdentifierToNumber(resultingDroppableZoneId)
                            if (onDragStartDroppableZoneId.current === ALL_ACTIVITIES && whichView === JOURNEY_DND_CONTAINER_WHICH_VIEW.createFromTravelComponents) {
                                updatedDraggedActivity.common.day = newDay
                                activityToJourneyStateWhenCreateJourneyFromTravelComponents(journeyDuration, updatedDraggedActivity)
                                updatedDndActivities.push(updatedDraggedActivity)
                            } else {
                                const {common: {groupId: movedActivityGroupId}} = updatedDraggedActivity
                                const forceMoveActivityGroup = _forceMoveActivityGroup(updatedDraggedActivity)
                                if ((altKeyPressed || movedActivityGroupId === null) && !forceMoveActivityGroup) {
                                    updatedDraggedActivity.common.day = newDay
                                    const indexOfDraggedActivity = updatedDndActivities.findIndex(updatedDndActivity => updatedDndActivity.common.id === draggedActivityId)
                                    if (indexOfDraggedActivity > -1) updatedDndActivities[indexOfDraggedActivity] = updatedDraggedActivity
                                } else {
                                    updatedDndActivities.filter(updatedDndActivity => updatedDndActivity.common.groupId === movedActivityGroupId)
                                        .sort((a, b) => {
                                            const dayA = a.common.day ?? Infinity
                                            const dayB = b.common.day ?? Infinity
                                            return dayA - dayB;
                                        })
                                        .forEach((updatedMovedDndActivity, index) => updatedMovedDndActivity.common.day = newDay + index)
                                }
                            }
                            updatedDndActivities = mergeTravelComponentsToJourneyElements(
                                cloneDeep(updatedDndActivities),
                                journeyDurationInDays,
                                false,
                                false
                            )
                            _onActivityMoveEnd(updatedDraggedActivity, updatedDndActivities, resultingDroppableZoneId, onDragStartDroppableZoneId.current)
                        }
                    }
                }
                _setDndActivities(updatedDndActivities)
                // only dispatch action if no recalculation is needed
                // if recalculation is needed, the return of the recalculation will dispatch action to update store
                !recalculateOnDifferentDate && _updateActivitiesObjectsBasedOnView(updatedDndActivities)
            }
        }
        onDragStartDroppableZoneId.current = null
        draggingActivityRef.current = null
        setDraggingActivity(null)
    }

    const _recalculateActivityOnDifferentDates = (activity: AllActivityTypes) => Boolean(journeyHasExactDates && recalculateActivityOnDifferentDate(activity.common.activityType))

    /**
     * Function that is executed when an Activity has been moved through clicking on an arrow from ALL_ACTIVITIES
     * in whichView === 'createFromTravelComponents' or by dnd.
     *
     * @param updatedMovedActivity Activity that has been moved
     * @param updatedDndActivities Data object
     * @param newDroppableZoneId Container to which the Activity has been moved. Is either a number (day) or ALL_ACTIVITIES
     * @param originDroppableZoneId Container from the the Activity was moved. Is either a number (day) or ALL_ACTIVITIES
     */
    const _onActivityMoveEnd = (
        updatedMovedActivity: AllActivityTypes,
        updatedDndActivities: AllActivityTypesArray,
        newDroppableZoneId: UniqueIdentifier,
        originDroppableZoneId: UniqueIdentifier | null,
    ) => {
        if (originDroppableZoneId === ALL_ACTIVITIES) removeRouteAndWaypointsFromCarBusActivity(updatedMovedActivity, updatedDndActivities)
        if (_recalculateActivityOnDifferentDates(updatedMovedActivity)) recalculateActivityEmissionWitNewGivenDay(
            updatedMovedActivity,
            newDroppableZoneId.toString()
        )
    }

    const _getNewDroppableZoneIdAndDay = (whichArrow: "up" | 'down', droppableZoneId: UniqueIdentifier) => {
        let newDay
        const droppableZoneIdNumber = convertUniqueIdentifierToNumber(droppableZoneId)
        if (whichArrow === 'up') {
            newDay = droppableZoneIdNumber - 1
        } else {
            newDay = droppableZoneIdNumber + 1
        }
        return newDay
    }

    const _undoDeleteActivityFromJourney = (activity: AllActivityTypes, queueId: string) => {
        const {common: {day, activityType}} = activity
        const journeyDurationInDays = getDurationInDaysOrNights(journeyDurationRef.current, false)
        if (journeyDurationInDays !== null && day !== null && day <= journeyDurationInDays) {
            dispatch(undoDeleteActivityFromJourney({queueId, updateActivities: true}))
        } else {
            _renderToast(t("TOASTS.WARNING.UNDO_DELETE_ACTIVITY_FROM_JOURNEY", {
                        activity: generateTranslatedActivityTypeString(activityType, i18n), day,
                        interpolation: {escapeValue: false}
                    }
                ), "warning"
            )
        }
    }

    const _renderDeleteActivityToast = (
        queueId: string,
        activity: AllActivityTypes
    ) => {
        let undoClicked = false;
        const {common: {activityType}} = activity
        const toastId = toast(
            <ToastWithButton
                message={t(
                    "TOASTS.SUCCESS.UNDO_DELETE_ACTIVITY_FROM_JOURNEY",
                    {activity: generateTranslatedActivityTypeString(activityType, i18n)}
                )}
                onClick={() => {
                    undoClicked = true
                    _undoDeleteActivityFromJourney(activity, queueId)
                    if (toastCount.current > 0) toastCount.current -= 1
                    toast.dismiss(toastId)
                }}/>,
            {
                progress: undefined,
                type: 'success',
            }
        );
        const unsubscribe = toast.onChange((payload: ToastItem) => {
            switch (payload.status) {
                case "added":
                    break;
                case "updated":
                    break;
                case "removed":
                    if (!undoClicked && payload.id === toastId) {
                        if (toastCount.current > 0) toastCount.current -= 1
                        dispatch(deleteActivityFromJourneyActivityQueue(queueId))
                    }
                    if (toastCount.current > 0) unsubscribe()
                    break;
            }
        })
    }

    /**
     * Deletes an activity if whichView === 'journeyOverview'. Either deletes a single Activity or a whole group of
     * Activities, depending on if the Alt Key was pressed or an Activity like car/bus has a route
     */
    const _onDeleteActivityJourneyOverview = (activityToDelete: AllActivityTypes) => {
        const {common: {groupId, activityType}} = activityToDelete
        const queueId = uuidv4()
        let deleteGroup = groupId !== null
        if (altKeyPressed) deleteGroup = false
        dispatch(deleteActivityFromJourney({
            activity: activityToDelete,
            queueId,
            deleteGroup
        }))
        _renderDeleteActivityToast(queueId, activityToDelete)
    }

    /**
     * Deletes an Activity if whichView === 'createFromTravelComponents'. If the Activity is deleted from the
     * ALL_ACTIVITIES container, it also gets deleted from all the other containers.
     */
    const _onDeleteActivityCreateFromTravelComponents = (activityToDelete: AllActivityTypes, dropbableZoneId: string) => {
        let updatedDndActivities = cloneDeep(dndActivitiesRef.current)
        const {common: {id, semanticId}} = activityToDelete
        if (dropbableZoneId === ALL_ACTIVITIES) {
            originalActivitiesForCreateFromTravelComponentsView.current = originalActivitiesForCreateFromTravelComponentsView.current.filter(originalActivity => originalActivity.common.id !== id)
            updatedDndActivities = updatedDndActivities.filter(activityToDelete => activityToDelete.common.semanticId !== semanticId)
        } else {
            updatedDndActivities = updatedDndActivities.filter(activityToDelete => activityToDelete.common.id !== id)
        }
        _setDndActivities(updatedDndActivities)
    }

    const _onDeleteActivity = (activityToDelete: AllActivityTypes, dropbableZoneId?: string) => {
        switch (whichView) {
            case JOURNEY_DND_CONTAINER_WHICH_VIEW.journeyOverview:
                _onDeleteActivityJourneyOverview(activityToDelete)
                break;
            case JOURNEY_DND_CONTAINER_WHICH_VIEW.createFromTravelComponents:
                if (typeof dropbableZoneId !== 'undefined') _onDeleteActivityCreateFromTravelComponents(activityToDelete, dropbableZoneId)
                break;
            default:
                whichView satisfies never
        }
    }

    /**
     * Adds activities based on common.activitiesToRecalculateOnDateChange to a given array.
     */
    const _addActivitiesForRecalculation = (activity: AllActivityTypes, activitiesForRecalculation: AllActivityTypesArray) => {
        const {common: {activityType}} = activity
        if (recalculateActivityOnDifferentDate(activityType)) activitiesForRecalculation.push(activity)
    }

    const _undoDeleteDayFromJourney = (queueId: string) => {
        // Gotta love JS
        // Because the way the toast is written, I cannot simply pass queueForDeleteDay via props of JourneyDnDContainer
        // and access in the toast, because when the toast is created, queueForDeleteDay is not set yet, making it when clicking
        // the undo button not available for _undoDeleteDayFromJourney(). Because of that, I have to fetch the store like this
        const state = store.getState()
        const {journey: {queueForDeleteDay}} = state
        if (queueForDeleteDay) {
            let updatedActivitiesToRecalculate: AllActivityTypesArray = []
            const {day: dayToAdd, activities: activitiesToAdd} = queueForDeleteDay[queueId]
            const updatedDndActivities = cloneDeep(dndActivitiesRef.current)
            const updatedActivitiesToAdd = cloneDeep(activitiesToAdd)
            const activityOnLastDayOfJourney = maxBy(updatedDndActivities, updatedDndActivity => updatedDndActivity.common.day)
            if (activityOnLastDayOfJourney) {
                const currentLastDayOfJourney = activityOnLastDayOfJourney.common.day
                if (currentLastDayOfJourney !== null) {
                    if (dayToAdd === 1) {
                        updatedDndActivities.forEach(updatedDndActivity => {
                            if (updatedDndActivity.common.day !== null) {
                                updatedDndActivity.common.day = updatedDndActivity.common.day + 1
                                _addActivitiesForRecalculation(updatedDndActivity, updatedActivitiesToRecalculate)
                            }
                        })
                    } else if (dayToAdd > currentLastDayOfJourney) {
                        if (dayToAdd + 1 !== currentLastDayOfJourney) updatedActivitiesToAdd.forEach(updatedDndActivity => {
                            const newLastDayOfJourney = currentLastDayOfJourney + 1
                            if (updatedDndActivity.common.day !== null) updatedDndActivity.common.day = newLastDayOfJourney
                        })
                    } else {
                        updatedDndActivities.forEach(updatedDndActivity => {
                            if (updatedDndActivity.common.day !== null) {
                                if (updatedDndActivity.common.day >= dayToAdd) {
                                    updatedDndActivity.common.day = updatedDndActivity.common.day + 1
                                    _addActivitiesForRecalculation(updatedDndActivity, updatedActivitiesToRecalculate)
                                }
                            }
                        })
                    }
                    activitiesToAdd.forEach(activityToAdd => updatedDndActivities.push(activityToAdd))

                }
            }
            _mergeAndDispatchReplaceActivitiesInJourney(updatedDndActivities, journeyDurationInDays)
            dispatch(undoDeleteDayFromJourney(queueId))
            dispatch(blockChangeJourneyDuration(false))
            recalculateActivitiesEmissionsWithUpdatedDateInActivityProps(updatedActivitiesToRecalculate)
        }
    }

    const _renderDeleteDayToast = (queueId: string, dayToDelete: number) => {
        let undoClicked = false;
        const toastId = toast(
            <ToastWithButton
                message={t('TOASTS.SUCCESS.UNDO_DELETE_DAY_FROM_JOURNEY', {day: dayToDelete})}
                onClick={() => {
                    undoClicked = true
                    avoidRecalculationThroughUseEffect.current = true
                    _undoDeleteDayFromJourney(queueId)
                    toast.dismiss(toastId)
                    if (toastCount.current > 0) toastCount.current -= 1
                }}/>,
            {
                progress: undefined,
                type: 'success',
            }
        )

        const unsubscribe = toast.onChange((payload: ToastItem) => {
            switch (payload.status) {
                case "added":
                    break;
                case "updated":
                    break;
                case "removed":
                    if (!undoClicked && payload.id === toastId) {
                        if (toastCount.current > 0) toastCount.current -= 1
                        dispatch(deleteDayFromJourneyDayQueue(queueId))
                        dispatch(blockChangeJourneyDuration(false))
                    }
                    if (toastCount.current === 0) unsubscribe()
                    break;
            }
        })
    }

    const _recalculateActivitiesOnDeleteDay = (updatedDnDActivities: AllActivityTypesArray, dayToDelete: number) => {
        const updatedActivitiesToRecalculate: AllActivityTypesArray = []
        updatedDnDActivities.forEach(updatedDndActivity => {
            if (updatedDndActivity.common.day !== null && updatedDndActivity.common.day >= dayToDelete) _addActivitiesForRecalculation(updatedDndActivity, updatedActivitiesToRecalculate)
        })
        recalculateActivitiesEmissionsWithUpdatedDateInActivityProps(updatedActivitiesToRecalculate)
    }

    /**
     * Re arranges days when an arrow is clicked and recalculates activity emissions if possible.
     *
     * @param movedDay The day that moved
     * @param whichArrow Indicating if day was moved up or down
     */
    const _onDayArrow = (movedDay: number, whichArrow: WhichArrowProps) => {
        const updatedDnDActivities = cloneDeep(dndActivitiesRef.current)
        let newDay: number = 0
        if (whichArrow === "up") {
            newDay = movedDay - 1
        } else {
            newDay = movedDay + 1
        }
        updatedDnDActivities.forEach(updatedDndActivity => {
            const {common: {day}} = updatedDndActivity
            if (day !== null) {
                if (whichArrow === "up") {
                    if (day === movedDay - 1) updatedDndActivity.common.day!! += 1
                    if (day === movedDay) updatedDndActivity.common.day!! -= 1
                } else {
                    if (day === movedDay + 1) updatedDndActivity.common.day!! -= 1
                    if (day === movedDay) updatedDndActivity.common.day!! += 1
                }
            }
        })
        const journeyDurationInDays = getDurationInDaysOrNights(journeyDuration, false)
        if (journeyDurationInDays !== null) {
            let activitiesToRecalculateOnDateChange: AllActivityTypesArray = []
            if (journeyHasExactDates) {
                addDatesToActivitiesWhenChangeDateOnDayChange(updatedDnDActivities, journeyDuration.dates!!.startDate)
                updatedDnDActivities.forEach(activityAtMovedDay => _addActivitiesForRecalculation(activityAtMovedDay, activitiesToRecalculateOnDateChange))
            } else {
                removeDatesFromActivitiesWhenChangeDateOnDayChange(updatedDnDActivities)
            }

            _mergeAndDispatchReplaceActivitiesInJourney(updatedDnDActivities, journeyDurationInDays)
            if (activitiesToRecalculateOnDateChange.length > 0) recalculateActivitiesEmissionsWithUpdatedDateInActivityProps(activitiesToRecalculateOnDateChange)
        }
    }

    const _setFieldsetHeight = () => {
        switch (whichView) {
            case JOURNEY_DND_CONTAINER_WHICH_VIEW.createFromTravelComponents:
                return "unset"
                break;
            case JOURNEY_DND_CONTAINER_WHICH_VIEW.journeyOverview:
                return '100%'
                break;
            default:
                whichView satisfies never
        }
    }

    return <Fieldset
        showLegend={false}
        legend={'LABELS.JOURNEY.CREATE_JOURNEY'}
        fieldsetStyles={{height: _setFieldsetHeight()}}
        fieldsetClasses={'journey-dnd-container-container'}>
        <DndContext sensors={sensors} onDragEnd={_onDragEnd} onDragStart={_onDragStart} onDragOver={_onDragOver}>
            {whichView === 'createFromTravelComponents' ? <CreateJourneyFromTravelComponents
                    hasExactDates={Boolean(journeyDuration.dates)}
                    draggingActivity={draggingActivity}
                    journeyDurationInDays={journeyDurationInDays}
                    isRecalculatingActivityEmissions={isLoading}
                    isOverAllActivitiesDroppableZone={draggingOverDay === ALL_ACTIVITIES}
                    onActivityArrow={_onActivityArrow}
                    originalActivities={originalActivitiesForCreateFromTravelComponentsView.current}
                    activities={dndActivities}
                    onDeleteActivity={_onDeleteActivity}/> :
                <JourneyOverview
                    travelComponentActivities={travelComponentActivities}
                    draggingActivity={draggingActivity}
                    hasExactDates={Boolean(journeyDuration.dates)}
                    altKeyPressed={altKeyPressed}
                    journeyDuration={journeyDuration}
                    activities={dndActivities}
                    isRecalculatingActivityEmissions={isLoading}
                    onDeleteActivity={_onDeleteActivity}
                    onDeleteDay={_deleteDay}
                    onDayArrow={_onDayArrow}
                    setAvoidRecalculationThroughUseEffect={() => avoidRecalculationThroughUseEffect.current = true}
                    onActivityArrow={_onActivityArrow}
                    journeyPersonCount={journeyPersonCount}/>
            }
            <DragOverlay dropAnimation={null}>
                {draggingActivity && <DraggingActivityListItem
                    altKeyPressed={altKeyPressed}
                    activities={activities}
                    whichView={whichView}
                    activity={draggingActivity}/>}
            </DragOverlay>
        </DndContext>
    </Fieldset>
}

export default JourneyDnDContainer